import _, { sum } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import api from 'src/api'
import {
  PenaltyScheme,
  TripTaxationFeesAmountsByProviderResponse,
  Fees_PolicyKindEnum,
  Fees_StatusEnum,
  Fees,
} from 'src/api/gen/api'
import { useProviders } from 'src/layout/hooks'

import { IProviderInfo } from './FeeSummaryAccordion'

interface IFeesSummaryPageProps {
  fees_scheme: PenaltyScheme
}

export const useFeesSummaryPage = function (props: IFeesSummaryPageProps) {
  const [loading, setLoading] = useState<boolean>(false)
  const [providerInfos, setProviderInfos] = useState<IProviderInfo[]>([])
  const { activeProviders } = useProviders()

  const policies_id = useMemo(() => props.fees_scheme.policies_names.map((policy_name) => policy_name.id), [])

  const computeProviderInfos = async (
    trip_taxation_amounts_list_promise: Promise<TripTaxationFeesAmountsByProviderResponse[]>,
    fees_list_promise: Promise<Fees[]>
  ) => {
    setLoading(true)

    let trip_taxation_amounts_list: TripTaxationFeesAmountsByProviderResponse[] = []
    let fees_list: Fees[] = []

    try {
      trip_taxation_amounts_list = await trip_taxation_amounts_list_promise
    } catch (e) {
      console.error(e)
    }

    try {
      fees_list = await fees_list_promise
    } catch (e) {
      console.error(e)
    }

    const fees_list_standardized: IProviderInfo[] = fees_list.map((fee: Fees) => ({
      providerId: fee.provider_id,
      providerName: fee.provider_name,
      amount: fee.amount,
      fees_count: 0,
      data: [fee],
    }))

    const trip_taxation_list_standardized: IProviderInfo[] = trip_taxation_amounts_list.map(
      (trip_taxation: TripTaxationFeesAmountsByProviderResponse) => ({
        providerId: trip_taxation.provider_id,
        providerName: trip_taxation.provider_name,
        amount: sum(trip_taxation.policies.map((p) => p.amount)),
        fees_count: 0,
        data: trip_taxation.policies.map((policy_elem) => {
          return {
            uid: '',
            provider_id: trip_taxation.provider_id,
            provider_name: trip_taxation.provider_name,
            policy_id: policy_elem.policy_id,
            policy_name: policy_elem.policy_name,
            policy_kind: Fees_PolicyKindEnum.trip_taxation,
            policy_start_datetime: policy_elem.policy_start_date,
            policy_end_datetime: policy_elem.policy_end_date,
            amount: policy_elem.amount,
            status: policy_elem.policy_status as Fees_StatusEnum,
            prorations: {},
          }
        }),
      })
    )

    const active_list_standardized: IProviderInfo[] = activeProviders
      .filter((activeProvider) => props.fees_scheme.providers.find((provider_id) => provider_id === activeProvider.id))
      .map((provider) => ({
        providerId: provider.id,
        providerName: provider.name,
        amount: 0,
        fees_count: 0,
        data: [],
      }))

    const reduced = [...fees_list_standardized, ...trip_taxation_list_standardized, ...active_list_standardized].reduce(
      (acc: { [key: string]: IProviderInfo }, elem) => {
        const data = (acc[elem.providerId]?.data ?? []).concat(elem.data)
        acc[elem.providerId] = {
          providerId: elem.providerId,
          providerName: elem.providerName,
          amount: (acc[elem.providerId]?.amount ?? 0) + elem.amount,
          fees_count: data.length,
          data: data,
        }
        return acc
      },
      {}
    )

    setProviderInfos(Object.values(reduced))
    setLoading(false)
  }

  useEffect(() => {
    const abortController = new AbortController()

    const trip_taxation_amounts_list = api.offenses.tripTaxationAmountsList({
      provider_ids: props.fees_scheme.providers,
      policy_ids: policies_id,
      trip_datetime_range_start: props.fees_scheme.start_date,
      trip_datetime_range_end: props.fees_scheme.end_date,
      options: { signal: abortController.signal },
    })

    const fees_list = api.fees.feesList({
      provider_ids: props.fees_scheme.providers,
      policy_ids: policies_id,
      policy_start_date: props.fees_scheme.start_date as unknown as string,
      policy_end_date: props.fees_scheme.end_date as unknown as string,
      options: { signal: abortController.signal },
    })

    computeProviderInfos(trip_taxation_amounts_list, fees_list)

    return () => abortController.abort()
  }, [])

  return { loading, providerInfos }
}
